import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { cloneDeep } from "lodash";
import { BehaviorSubject, catchError, from, map, Observable, of, switchMap, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { IPayload, Payload } from "../interface/filters";
import { RecoveryPlan } from "../modules/pages/hot-alert/configurations/recovery-plan";
import { RecoveryPlanAdapter } from "../modules/pages/hot-alert/configurations/recovery-plan-adapter";
import { genRecoveryPlanTableConfig } from "../modules/pages/hot-alert/configurations/recovery-table-config";
import { RecoveryPlanDialogComponent } from "../modules/pages/hot-alert/recovery-plan-dialog/recovery-plan-dialog.component";
import { RecoveryPlanInfoComponent } from "../modules/pages/hot-alert/recovery-plan-info/recovery-plan-info.component";
import { IApiService } from "../modules/records/models/api/i-api-service";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from "../shared/dialogs/confirm-dialog/confirm-dialog.component";
import { ApplicationService } from "./application.service";
import { MyUtils } from "./my-utils.service";
import { delay } from "rxjs/operators";

interface RecoveryPlanDialogData {
  surveyId: string;
  completedDate: string;
  outletId: string;
  isEdit: boolean;
  brand: string;
  title: string;
  description: string;
  dateEnd: Date | string;
}

@Injectable({
  providedIn: "root",
})
export class RecoveryPlanService implements IApiService<RecoveryPlan> {
  endpoint = environment.csibefrontend;
  isLoading = false;
  totalRecords: number = 0;
  lastPageIndexQuery: number = 0;
  lastSort: string | null = null;
  private _reports = new BehaviorSubject<RecoveryPlan[]>([]);
  get item$() {
    return this._reports.asObservable();
  }

  constructor(
    private http: HttpClient,
    private adapter: RecoveryPlanAdapter,
    private utils: MyUtils,
    private applicationService: ApplicationService,
    private dialog: MatDialog
  ) { }

  getAll(
    filter: IPayload,
    skip: number,
    limit: number,
    sortDirection: string,
    sortBy: string,
    extraFilters: {
      NPS: string;
      NSAT: string;
      channel: string;
      searchBy: string;
      searchFor: string;
      recovery: string;
    }) {
    this.totalRecords = 0;

    let payload = this.buildFilters(filter, skip, limit, sortDirection, sortBy, extraFilters);
    this.isLoading = true;

    const endpointPath = this.applicationService.navBarSelectValue.idItem.startsWith("CEC") ?
      "v4/getRecoveryPlanList" : "roar-reports/v1/recovery-plan/data";

    return this.http.post(this.endpoint + endpointPath, payload).pipe(
      map((res) => this.adapter.mapCollection(res)),
      tap((res) => this._reports.next(res.items)),
      tap((_) => (this.isLoading = false)),
      catchError((err) => {
        this.isLoading = false;
        return of(this.adapter.mapCollection());
      })
    );
  }

  private buildFilters(
    filter: any,
    skip: number,
    limit?: number,
    sortDirection?: string,
    sortBy?: string,
    extraFilters?: {
      NPS: string;
      NSAT: string;
      recovery: string;
      channel: string;
      searchBy: string;
      searchFor: string;
    }
  ) {
    let payload: IPayload = Object.assign({}, filter || {});
    let isNextPage = skip > this.lastPageIndexQuery;

    payload.question = {
      limit: "",
      locale: this.applicationService.languageSelectValue,
      orderByScore: "",
      questionCode: "",
      section: this.applicationService.navBarSelectValue.idItem,
      tab: this.applicationService.navBarSelectValue.idItem,
    };

    payload.extraFilters = {
      OrderByColumn: (sortBy?.startsWith('stellantis-') ? '$$' + sortBy : sortBy) ?? "",
      Page: skip ? skip + 1 : 1,
      ItemsPerPage: limit,
      OrderBy: sortDirection || "",
      SearchColumn: extraFilters && extraFilters.searchBy ? extraFilters.searchBy : "",
      SearchFor: extraFilters && extraFilters.searchFor ? extraFilters.searchFor : "",
      Nps: extraFilters?.NPS || extraFilters?.NSAT || "",
      RpCreate: extraFilters?.recovery && extraFilters?.recovery === "Recovery" ? 1 : 0 || "",
      Channel: extraFilters?.channel || "",
      lastSort: isNextPage ? this.lastSort : null
    };

    return payload;
  }

  delete(el: RecoveryPlan) {

    this.isLoading = true;
    return this.sendDeletePlanRequest(el).pipe(
      delay(1000),
      tap(p => this.isLoading = false),
      catchError((err) => {
        this.isLoading = false;
        throw err;
      })
    );
  }

  print(el: RecoveryPlan) {
    return of("printed");
  }

  downloadExcel(el: RecoveryPlan) {
    let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
    payload.question.section = this.applicationService.navBarSelectValue.idItem;
    payload.question.locale = this.applicationService.languageSelectValue;

    const endpointPath = this.applicationService.navBarSelectValue.idItem.startsWith("CEC") ?
      "v4/exportRecoveryPlan" : "roar-reports/v1/recovery-plan";

    return this.http
      .post(this.endpoint + endpointPath, payload, {
        responseType: "blob",
      })
      .pipe(
        tap((data) => {
          const file = new Blob([data], { type: "text/csv;charset=utf-8" });
          const fileURL = URL.createObjectURL(file);
          const a = document.createElement("a");
          a.href = fileURL;
          a.download = "exportRecoveryPlan" + ".xlsx";
          a.target = "_blank";
          a.click();
        })
      );
  }

  getRecoveryPlanAttach(brand: string, region: string, recoveryPlanId: string) {

    let section = this.applicationService.navBarSelectValue.name;

    let endpointQ = `v1/getRecoveryPlanAttach?brand=${brand}&region=${region}&section=${section}&recoveryPlanId=${recoveryPlanId}`;

    return this.http
      .get(
        this.endpoint +
        endpointQ, {}
      )
      .pipe(
        tap((data: any) => {

          const linkSource = `data:${data.type};base64,${data.file}`;
          const downloadLink = document.createElement("a");
          let extension = ''
          switch (data.type) {
            case 'application/msword': extension = '.doc'; break;
            case 'text/csv': extension = '.text'; break;
            case 'application/pdf': extension = '.pdf'; break;
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': extension = '.xlsx'; break
            default:
              break;
          }
          const fileName = `recovery_plan${extension}`;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
        })
      );
  }

  closePlan(el: RecoveryPlan, formValue: any) {
    let body = this.adapter.generateCloseBody(el, formValue);
    this.isLoading = true;
    return this.http.post(this.endpoint + "v4/closeRecoveryPlan", body).pipe(
      delay(1000),
      tap(p => this.isLoading = false),
      catchError((err) => {
        this.isLoading = false;
        throw err;
      })
    );
  }

  createPlan(data: RecoveryPlanDialogData) {
    return this._showRecoveryPlanDialog(data).pipe(
      switchMap((formData) => (formData ? this._sendCreatePlanRequest(data, formData) : of(null)))
    );
  }

  editPlan(el: RecoveryPlan) {
    this.isLoading = true;
    return this._showRecoveryPlanDialog({
      surveyId: el.surveyRequestId,
      completedDate: el.completedDate,
      outletId: el.outletId,
      isEdit: true,
      title: el.title,
      description: el.description,
      dateEnd: el.dateEnd,
      additionalInformation: el.additionalInformation,
      attach: el.attach,
      brand: el.brandId,
      region: el.regionId,
      recoveryPlanId: el.recoveryPlanId,
      closed: el.status === "Closed"
    }).pipe(
      switchMap((data) => (data ? this._sendEditPlanRequest(el, data)
        .pipe(
          delay(1000),
          switchMap((r) => {

            let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
            payload.question.section = this.applicationService.navBarSelectValue.idItem;
            payload.question.locale = this.applicationService.languageSelectValue;
            return this.get(payload);
          }),

          catchError((err) => {


            return of([]);
          })
        ) : of(null))
      ),
      tap((_) => this.isLoading = false));
  }


  get(filter: Payload) {
    return this.http
      .post<any>(
        this.endpoint + "v4/getRecoveryPlanList",
        filter
      )
      .pipe(
        map((res) => res),
        catchError((err) => {
          return of({} as any);
        })
      );
  }

  private _sendEditPlanRequest(el: RecoveryPlan, form: any) {

    let editedplan = this.adapter.generateEditBody(el, form);

    const endpointPath = this.applicationService.navBarSelectValue.idItem.startsWith("CEC") ?
      "v4/updateRecoveryPlan" : "v4/updateRecoveryPlanElastic";

    let b64filePromise = form.file ? this.getBase64(form.file) : of(undefined);
    let b64file$: Observable<string | undefined> = from(b64filePromise);
    return b64file$.pipe(
      map((b64file) => ({ ...editedplan, file: b64file, type: form.file?.type })),
      switchMap((body) => this.http.post(this.endpoint + endpointPath, body))
    );
  }

  private _sendCreatePlanRequest(data: RecoveryPlanDialogData, form: any) {

    if (data.brand.indexOf("b_") < 0) {
      data.brand = "b_" + data.brand;
    }

    let newPlan = this.adapter.generateCreateBody(

      data.surveyId,
      data.brand,
      data.completedDate,
      form
    );

    const endpointPath = this.applicationService.navBarSelectValue.idItem.startsWith("CEC") ?
      "v4/addRecoveryPlan" : "v4/addRecoveryPlanElastic";

    let b64filePromise = form.file ? this.getBase64(form.file) : of(undefined);
    let b64file$: Observable<string | undefined> = from(b64filePromise);
    return b64file$.pipe(
      map((b64file) => ({ ...newPlan, file: b64file, type: form.file?.type })),
      switchMap((body) => this.http.post(this.endpoint + endpointPath, body))
    );
  }

  private _showRecoveryPlanDialog(data: {
    surveyId: string;
    completedDate: string;
    outletId: string;
    isEdit: boolean;
    title: string;
    description: string;
    dateEnd: Date | string;
    additionalInformation?: any;
    attach?: boolean;
    brand?: string;
    region?: string;
    recoveryPlanId?: string;
    closed?: boolean
  }) {
    return this.dialog
      .open<
        RecoveryPlanDialogComponent,
        {
          surveyId: string;
          completedDate: string;
          outletId: string;
          isEdit: boolean;
          title: string;
          description: string;
          dateEnd: Date | string;
          additionalInformation?: any;
          attach?: boolean;
          brand?: string;
          region?: string;
          recoveryPlanId?: string;
        }
      >(RecoveryPlanDialogComponent, {
        data: data,
        maxWidth: "100vh",
        maxHeight: "100vh",
        panelClass: "recoveryPlanDialog",
      })
      .afterClosed();
  }

  private _showDialogConfirm(config: MatDialogConfig<ConfirmDialogData>) {
    return this.dialog.open(ConfirmDialogComponent, config).afterClosed();
  }

  private sendDeletePlanRequest(el: RecoveryPlan) {
    let body = this.adapter.generateDeleteBody(el);
    return this.http.post(this.endpoint + "v4/deleteRecoveryPlan", body);
  }

  genTableConfig() {
    return genRecoveryPlanTableConfig(this);
  }

  private getBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        let b64 = reader.result as string;
        let ix = b64.indexOf(";base64,");
        if (ix >= 0) {
          let substr = b64.substring(ix);
          let data = substr.replace(";base64,", "");
          return resolve(data);
        } else {
          resolve(reader.result as string);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }

  infoPlan(el: any) {
    return this.dialog
      .open(RecoveryPlanInfoComponent, {
        maxHeight: "100vh",
        maxWidth: "100vh",
        data: el,
      })
      .afterClosed()
      .pipe(switchMap((formData) => of(null)));
  }
}
